import type {ReactElement} from 'react';
import {useState} from 'react';
import type {EntityThumbnail, EntityThumbnailTileProps} from './entity-thumbnail-tile.types';
import {ENTITY_THUMBNAIL_COUNT_BY_VARIANT, EntityThumbnailTileVariant} from './entity-thumbnail-tile.types';
import styles from './entity-thumbnail-tile.module.scss';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {Text} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';

/** A titled row of entity thumbnails under an agent reply, with a view-more action; a single summary card on a small chat. */
export function EntityThumbnailTile<TThumbnail extends EntityThumbnail>(props: EntityThumbnailTileProps<TThumbnail>): ReactElement | null {
  const {isSmall} = useAiChatSize();
  const [failedThumbnailIds, setFailedThumbnailIds] = useState<Set<string>>(new Set());
  const displayThumbnails = props.thumbnails.filter((thumbnail) => !failedThumbnailIds.has(thumbnail.id)).slice(0, ENTITY_THUMBNAIL_COUNT_BY_VARIANT[props.variant]);

  if (displayThumbnails.length < props.minThumbnailsToRender) {
    return null;
  }

  const handleThumbnailLoadError = (thumbnailId: string): void => {
    setFailedThumbnailIds((prev) => {
      if (prev.has(thumbnailId)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(thumbnailId);

      return next;
    });
  };

  const getViewMoreButton = (): ReactElement => {
    return (
      <div className={styles.viewMoreWrapper}>
        <Button type={ButtonType.SECONDARY} size={ButtonSize.SMALL} text={props.viewMoreAction.label} icon={props.viewMoreAction.icon} onClick={props.viewMoreAction.onClick} customClasses={'radius-8'} />
      </div>
    );
  };

  const getHeaderDescription = (): ReactElement => {
    return (
      <div className={styles.headerText}>
        <Text className={styles.title} val={props.title} />
        <div className={styles.subtitleRow}>
          <Text className={styles.subtitle} val={props.subtitle} />
          <Icon icon={props.subtitleIcon} size={IconSize.SIZE_ICON_16} />
        </div>
      </div>
    );
  };

  const getHeader = (): ReactElement => {
    return (
      <div className={styles.header}>
        {getHeaderDescription()}
        <div className={styles.headerActions}>
          {getViewMoreButton()}
        </div>
      </div>
    );
  };

  const getThumbnail = (thumbnail: TThumbnail): ReactElement => {
    return (
      <ClickableDiv
        key={thumbnail.id}
        className={styles.thumbnail}
        onClick={(): void => {
          props.onThumbnailClick(thumbnail);
        }}
      >
        <img
          className={styles.thumbnailImage}
          src={thumbnail.previewUrl}
          alt={thumbnail.altText}
          onError={(): void => {
            handleThumbnailLoadError(thumbnail.id);
          }}
        />
        <div className={styles.hoverOverlay}>
          <Icon icon={props.hoverAction.icon} size={IconSize.SIZE_ICON_16} type={props.hoverAction.iconType} className={styles.hoverIcon} />
          <Text className={styles.hoverText} val={props.hoverAction.label} />
        </div>
      </ClickableDiv>
    );
  };

  const getMobileIllustration = (): ReactElement | null => {
    if (!props.illustrationUrl) {
      return null;
    }

    return <img className={styles.mobileTileImage} src={props.illustrationUrl} alt="" />;
  };

  const getMobileTile = (): ReactElement => {
    return (
      <ClickableDiv className={styles.mobileTileCard} onClick={props.viewMoreAction.onClick}>
        <div className={styles.mobileTextContent}>
          <Text className={styles.title} val={props.title} />
          <Text className={styles.subtitle} val={props.mobileSubtitle} />
        </div>
        {getMobileIllustration()}
      </ClickableDiv>
    );
  };

  const getVariantClass = (): string => {
    if (props.variant === EntityThumbnailTileVariant.HALF) {
      return styles.half;
    }

    return styles.full;
  };

  const getDesktopTile = (): ReactElement => {
    return (
      <div className={`${styles.tileCard} ${getVariantClass()}`} role="presentation">
        {getHeader()}
        <div className={styles.thumbnailsRow}>{displayThumbnails.map(getThumbnail)}</div>
      </div>
    );
  };

  if (isSmall) {
    return getMobileTile();
  }

  return getDesktopTile();
}
